import {
  clamp,
  quatConjugate,
  quatMul,
  quatYaw,
  slerp,
  smoothstep,
  type Quat,
  type Vec3,
  wrap,
  yawQuat,
} from "../math";
import type { PlannerConfig } from "../types";

export const REFERENCE_FPS = 50;

export interface ReferenceSample {
  jointPos: Float32Array;
  jointVel: Float32Array;
  pelvisQuat: Quat;
  rootLinVel: Vec3;
  rootAngVel: Vec3;
  contact: Float32Array;
}

export interface TransitionSpec {
  from: ReferenceSample;
  to: ReferenceSample;
  steps: number;
}

export function stillSample(
  joints: ArrayLike<number>,
  quat: ArrayLike<number>,
  velocity?: ArrayLike<number>,
): ReferenceSample {
  return {
    jointPos: Float32Array.from(joints),
    jointVel: velocity ? Float32Array.from(velocity) : new Float32Array(29),
    pelvisQuat: [quat[0] ?? 1, quat[1] ?? 0, quat[2] ?? 0, quat[3] ?? 0],
    rootLinVel: [0, 0, 0],
    rootAngVel: [0, 0, 0],
    contact: new Float32Array(12),
  };
}

function alignedTarget(a: ArrayLike<number>, b: ArrayLike<number>): Quat {
  let dot = 0;
  for (let i = 0; i < 4; i += 1) dot += (a[i] ?? 0) * (b[i] ?? 0);
  const sign = dot < 0 ? -1 : 1;
  return [
    sign * (b[0] ?? 1),
    sign * (b[1] ?? 0),
    sign * (b[2] ?? 0),
    sign * (b[3] ?? 0),
  ];
}

function monotoneTangents(delta: number, v0: number, v1: number, seconds: number): [number, number] {
  if (Math.abs(delta) < 1e-7) return [0, 0];
  const sign = Math.sign(delta);
  let m0 = clamp(seconds * v0 * sign, 0, 3 * Math.abs(delta));
  let m1 = clamp(seconds * v1 * sign, 0, 3 * Math.abs(delta));
  const radius = Math.hypot(m0 / Math.abs(delta), m1 / Math.abs(delta));
  if (radius > 3) {
    const scale = 3 / radius;
    m0 *= scale;
    m1 *= scale;
  }
  return [sign * m0, sign * m1];
}

export function transitionSteps(
  from: ReferenceSample,
  to: ReferenceSample,
  jointRate: number,
  yawRateDeg: number,
  minSeconds: number,
  maxSeconds: number,
): number {
  let jointDelta = 0;
  for (let joint = 0; joint < 29; joint += 1) {
    jointDelta = Math.max(jointDelta, Math.abs(
      (to.jointPos[joint] ?? 0) - (from.jointPos[joint] ?? 0),
    ));
  }
  const yawDelta = Math.abs(wrap(quatYaw(to.pelvisQuat) - quatYaw(from.pelvisQuat)));
  const seconds = 1.5 * Math.max(
    jointDelta / Math.max(jointRate, 1e-6),
    yawDelta / Math.max(yawRateDeg * Math.PI / 180, 1e-6),
  );
  return Math.max(1, Math.round(clamp(
    Math.ceil(seconds * REFERENCE_FPS),
    minSeconds * REFERENCE_FPS,
    maxSeconds * REFERENCE_FPS,
  )));
}

export function makeTransition(
  from: ReferenceSample,
  to: ReferenceSample,
  jointRate: number,
  yawRateDeg: number,
  minSeconds: number,
  maxSeconds: number,
): TransitionSpec {
  return {
    from,
    to,
    steps: transitionSteps(from, to, jointRate, yawRateDeg, minSeconds, maxSeconds),
  };
}

export function transitionSample(spec: TransitionSpec, step: number): ReferenceSample {
  const phase = clamp(step / Math.max(spec.steps, 1), 0, 1);
  const seconds = Math.max(spec.steps, 1) / REFERENCE_FPS;
  const phase2 = phase * phase;
  const phase3 = phase2 * phase;
  const h00 = 2 * phase3 - 3 * phase2 + 1;
  const h10 = phase3 - 2 * phase2 + phase;
  const h01 = -2 * phase3 + 3 * phase2;
  const h11 = phase3 - phase2;
  const g00 = 6 * phase2 - 6 * phase;
  const g10 = 3 * phase2 - 4 * phase + 1;
  const g01 = -6 * phase2 + 6 * phase;
  const g11 = 3 * phase2 - 2 * phase;
  const jointPos = new Float32Array(29);
  const jointVel = new Float32Array(29);
  for (let joint = 0; joint < 29; joint += 1) {
    const p0 = spec.from.jointPos[joint] ?? 0;
    const p1 = spec.to.jointPos[joint] ?? p0;
    const [m0, m1] = monotoneTangents(
      p1 - p0,
      spec.from.jointVel[joint] ?? 0,
      spec.to.jointVel[joint] ?? 0,
      seconds,
    );
    jointPos[joint] = h00 * p0 + h10 * m0 + h01 * p1 + h11 * m1;
    jointVel[joint] = (g00 * p0 + g10 * m0 + g01 * p1 + g11 * m1) / seconds;
  }

  const target = alignedTarget(spec.from.pelvisQuat, spec.to.pelvisQuat);
  const u = smoothstep(phase);
  const pelvisQuat = slerp(spec.from.pelvisQuat, target, u);
  const relative = quatMul(quatConjugate(spec.from.pelvisQuat), target);
  const half = Math.acos(clamp(relative[0], -1, 1));
  const sinHalf = Math.sin(half);
  const rate = 6 * phase * (1 - phase) / seconds;
  const rootAngVel: Vec3 = sinHalf > 1e-6
    ? [
        relative[1] / sinHalf * 2 * half * rate,
        relative[2] / sinHalf * 2 * half * rate,
        relative[3] / sinHalf * 2 * half * rate,
      ]
    : [0, 0, 0];

  return {
    jointPos,
    jointVel,
    pelvisQuat,
    rootLinVel: [0, 0, 0],
    rootAngVel,
    contact: new Float32Array(12),
  };
}

export function scanHoldSteps(yawOffset: number, cfg: PlannerConfig): number {
  if (!cfg.smooth_scan || Math.abs(yawOffset) < 1e-6) return cfg.scan_steps;
  const distance = Math.abs(yawOffset);
  const vmax = (cfg.scan_yaw_rate_deg ?? 45) * Math.PI / 180;
  const accel = (cfg.scan_yaw_accel_deg ?? 180) * Math.PI / 180;
  const accelDistance = vmax * vmax / accel;
  const seconds = distance <= accelDistance
    ? 2 * Math.sqrt(distance / accel)
    : distance / vmax + vmax / accel;
  const ramp = Math.max(2, Math.ceil(seconds * REFERENCE_FPS));
  return ramp + 2 * cfg.hold_tail;
}

export function scanSample(
  yawOffset: number,
  anchorQuat: Quat,
  step: number,
  hold: number,
  cfg: PlannerConfig,
): { quat: Quat; rate: number } {
  const ramp = Math.max(1, hold - 2 * cfg.hold_tail);
  if (!cfg.smooth_scan) {
    const phase = clamp(step / ramp, 0, 1);
    return {
      quat: quatMul(yawQuat(yawOffset * phase), anchorQuat),
      rate: step < ramp ? yawOffset * REFERENCE_FPS / ramp : 0,
    };
  }

  const distance = Math.abs(yawOffset);
  const seconds = ramp / REFERENCE_FPS;
  const accel = (cfg.scan_yaw_accel_deg ?? 180) * Math.PI / 180;
  const disc = Math.max(0, seconds * seconds - 4 * distance / accel);
  const accelTime = 0.5 * (seconds - Math.sqrt(disc));
  const peak = accel * accelTime;
  const time = Math.min(step / REFERENCE_FPS, seconds);
  let travelled = distance;
  let speed = 0;
  if (time < accelTime) {
    travelled = 0.5 * accel * time * time;
    speed = accel * time;
  } else if (time < seconds - accelTime) {
    travelled = 0.5 * accel * accelTime * accelTime + peak * (time - accelTime);
    speed = peak;
  } else if (time < seconds) {
    const left = seconds - time;
    travelled = distance - 0.5 * accel * left * left;
    speed = accel * left;
  }
  const sign = Math.sign(yawOffset) || 1;
  return {
    quat: quatMul(yawQuat(sign * travelled), anchorQuat),
    rate: sign * speed,
  };
}
